import { type ReactNode, useId, useMemo, useState } from 'react';
import { Check, ChevronsUpDown, type LucideIcon, Search } from 'lucide-react';

import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/Command/Command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover/Popover';
import { cn } from '@/util/utils';

export interface ComboboxOption {
    value: string;
    label: string;
    /** Extra text after the label, muted. */
    description?: string;
    /** More text the search matches, not shown: a phone number the row has no room for. */
    keywords?: string;
    /** Listed, so it can be found and its description read, but not chosen. */
    disabled?: boolean;
    icon?: ReactNode;
}

/** A command at the foot of the list, outside the options: "Add a new client". */
export interface ComboboxAction {
    label: string;
    icon?: ReactNode;
    onSelect: () => void;
}

export interface ComboboxGroup {
    label: string;
    options: ComboboxOption[];
}

export interface ComboboxProps {
    id?: string;
    value: string | null;
    onValueChange: (value: string | null) => void;
    /** Flat, or grouped; a group survives a search when its label or any option matches. */
    options: ComboboxOption[] | ComboboxGroup[];
    placeholder?: string;
    searchPlaceholder?: string;
    emptyText?: string;
    size?: 'default' | 'sm';
    disabled?: boolean;
    invalid?: boolean;
    className?: string;
    /** For a combobox with no visible label, such as one standing in a filter bar. */
    'aria-label'?: string;
    /** A help line drawn outside the field, read with it. */
    'aria-describedby'?: string;
    /**
     * How many options to put in the DOM at once. The rest are reachable by typing.
     *
     * <p>A register can be far larger than a list anyone scrolls: brands is 3,074 rows. Mounting
     * all of them costs about two seconds on a laptop and the panel is unusable long before that,
     * so the list is cut and the footer says how much was cut. Raise it for a list that is long
     * but bounded, where seeing everything is the point.</p>
     */
    maxVisible?: number;
    /** Offered whatever the search, so the thing looked for can be made when it is not there. */
    action?: ComboboxAction;
    /**
     * Drawn as the design's search box (ui-input with icon="search"): the magnifier before the
     * text and no chevron, for a field whose job is finding one record among hundreds, such as
     * New order's customer. The panel is the same either way.
     */
    search?: boolean;
    /**
     * A leading icon naming what the list filters by, as the design's filter selects carry one
     * (the dashboard's Customer filter). Ignored by the search look, which has its own.
     */
    icon?: LucideIcon;
}

/** Enough to fill the panel several times over, cheap enough to mount in a frame. */
const DEFAULT_MAX_VISIBLE = 100;

function isGrouped(options: ComboboxOption[] | ComboboxGroup[]): options is ComboboxGroup[] {
    return options.length > 0 && 'options' in options[0];
}

/**
 * ui-select[search], READ 2092:5770: the design's Combobox, used where a list is long enough
 * that scrolling is the wrong answer. The trigger is the input control with ChevronsUpDown at
 * half strength; a chosen value is set in medium. The panel is the search palette's: a search
 * row over grouped options, the tick on the right of the selected one (G-056, one side).
 */
function Combobox({
    id,
    value,
    onValueChange,
    options,
    placeholder = 'Select something…',
    searchPlaceholder = 'Search…',
    emptyText = 'No results found.',
    size = 'default',
    disabled,
    invalid,
    className,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    maxVisible = DEFAULT_MAX_VISIBLE,
    action,
    search: looksLikeSearch = false,
    icon: Icon,
}: ComboboxProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const generatedId = useId();
    const triggerId = id ?? generatedId;
    const groups: ComboboxGroup[] = useMemo(
        () => (isGrouped(options) ? options : [{ label: '', options }]),
        [options],
    );
    const selected = groups.flatMap((g) => g.options).find((o) => o.value === value);

    /*
      Filtered here rather than by cmdk, because cmdk filters what is already mounted and the
      mounting is the expensive part. `shouldFilter={false}` below hands it the short list.
      The chosen option is always kept, so what is selected never vanishes from its own list.
    */
    const { visible, hidden } = useMemo(() => {
        const term = search.trim().toLowerCase();
        const matches = (option: ComboboxOption, groupLabel: string) =>
            term === '' ||
            `${option.label} ${option.description ?? ''} ${option.keywords ?? ''} ${groupLabel}`
                .toLowerCase()
                .includes(term);

        const found = groups.map((group) => ({
            ...group,
            options: group.options.filter((option) => matches(option, group.label)),
        }));
        const total = found.reduce((count, group) => count + group.options.length, 0);
        const cut = found.reduce<{ groups: ComboboxGroup[]; shown: number }>(
            (acc, group) => {
                const kept = group.options.slice(0, Math.max(0, maxVisible - acc.shown));
                // Whatever is chosen stays reachable even when the cut would have dropped it.
                const withSelected =
                    selected !== undefined &&
                    group.options.includes(selected) &&
                    !kept.includes(selected)
                        ? [selected, ...kept]
                        : kept;
                return {
                    groups: [...acc.groups, { ...group, options: withSelected }],
                    shown: acc.shown + withSelected.length,
                };
            },
            { groups: [], shown: 0 },
        );
        return { visible: cut.groups, hidden: total - cut.shown };
    }, [groups, search, maxVisible, selected]);

    return (
        /*
          `modal` so the panel scrolls when the combobox is inside a sheet or a dialog. Those lock
          scrolling with react-remove-scroll, the panel is portalled outside their DOM, and the
          lock then swallows every wheel event over it: the list scrolls programmatically and by
          keyboard but not by the wheel, which reads as a frozen dropdown. A modal popover brings
          its own scroll handling and is exempt.
        */
        <Popover open={open} onOpenChange={setOpen} modal>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    id={triggerId}
                    role="combobox"
                    aria-label={ariaLabel}
                    aria-describedby={ariaDescribedBy}
                    aria-expanded={open}
                    aria-invalid={invalid || undefined}
                    disabled={disabled}
                    data-slot="combobox-trigger"
                    className={cn(
                        'bg-card border-input text-foreground flex w-full min-w-0 items-center gap-2 rounded-md border text-left text-sm shadow-xs',
                        looksLikeSearch || Icon ? 'px-3' : 'px-4',
                        size === 'sm' ? 'h-8' : 'h-9',
                        // The Select's touch size, so the two read as one control on a phone.
                        'pointer-coarse:min-h-11 pointer-coarse:text-base',
                        'focus-visible:border-ring transition-[color,box-shadow,border-color] duration-(--duration-fast) outline-none focus-visible:shadow-[0_0_0_3px_var(--outline)]',
                        'aria-invalid:border-destructive aria-invalid:focus-visible:border-destructive disabled:bg-muted disabled:text-text-locked disabled:cursor-not-allowed disabled:shadow-none aria-invalid:focus-visible:shadow-[0_0_0_3px_var(--outline-destructive)]',
                        className,
                    )}
                >
                    {looksLikeSearch ? (
                        <Search
                            aria-hidden
                            className="text-muted-foreground size-[15px] shrink-0"
                        />
                    ) : Icon ? (
                        <Icon aria-hidden className="text-muted-foreground size-4 shrink-0" />
                    ) : null}
                    <span
                        className={cn(
                            // The placeholder is in the content colour too, as the prototype's
                            // ui-select__label draws both; a search box's is the input's muted one.
                            'min-w-0 flex-1 truncate',
                            selected && !looksLikeSearch && 'font-medium',
                            !selected && looksLikeSearch && 'text-muted-foreground',
                        )}
                    >
                        {selected?.label ?? placeholder}
                    </span>
                    {looksLikeSearch ? null : (
                        <ChevronsUpDown
                            aria-hidden
                            className="text-muted-foreground size-4 shrink-0 opacity-50"
                        />
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="w-(--radix-popover-trigger-width) min-w-[220px] p-1"
            >
                <Command shouldFilter={false}>
                    <CommandInput
                        placeholder={searchPlaceholder}
                        value={search}
                        onValueChange={setSearch}
                        // Bleeds out of the panel's 4px inset so its rule spans the full width.
                        wrapperClassname="-mx-1 -mt-1 mb-1"
                    />
                    {/* 392 is the whole panel in the prototype; the search row (41), the 4px under
                        it, the panel's bottom inset and its border take the other 51. */}
                    <CommandList className="max-h-[341px]">
                        <CommandEmpty>{emptyText}</CommandEmpty>
                        {/* cmdk's empty state counts mounted items, and the action is one, so
                            with an action the no-match line is drawn here instead. */}
                        {action && visible.every((group) => group.options.length === 0) ? (
                            <p
                                data-slot="command-empty"
                                className="text-muted-foreground px-2 py-1.5 text-sm font-normal tracking-normal"
                            >
                                {emptyText}
                            </p>
                        ) : null}
                        {visible.map((group) => (
                            <CommandGroup key={group.label} heading={group.label || undefined}>
                                {group.options.map((option) => (
                                    <CommandItem
                                        key={option.value}
                                        value={`${option.label} ${option.description ?? ''} ${option.keywords ?? ''} ${group.label} ${option.value}`}
                                        disabled={option.disabled}
                                        onSelect={() => {
                                            onValueChange(
                                                option.value === value ? null : option.value,
                                            );
                                            setOpen(false);
                                        }}
                                    >
                                        {option.icon}
                                        {/*
                                          Stacked, not side by side. On one line the label
                                          carried flex-1, whose basis is 0%, while the
                                          description had no flex rule and so took its full
                                          content width first, leaving the label the remainder:
                                          in the app version log's person filter a name laid out
                                          at 12px beside an email that was never clipped
                                          (ALS-183), and the commodity code picker lost its code
                                          to a long description the same way. The label is the
                                          identifier, so it keeps its own line.
                                        */}
                                        <span className="flex min-w-0 flex-1 flex-col">
                                            <span className="truncate">{option.label}</span>
                                            {option.description ? (
                                                <span className="text-muted-foreground truncate text-xs">
                                                    {option.description}
                                                </span>
                                            ) : null}
                                        </span>
                                        <Check
                                            aria-hidden
                                            className={cn(
                                                'size-3.5 shrink-0 text-current',
                                                option.value === value
                                                    ? 'opacity-100'
                                                    : 'opacity-0',
                                            )}
                                        />
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        ))}
                        {hidden > 0 ? (
                            <p className="text-muted-foreground border-border border-t px-3 py-2 text-xs">
                                {`${hidden} more. Type to narrow the list.`}
                            </p>
                        ) : null}
                        {action ? (
                            /* Its own group, so cmdk's arrow keys reach it after the last option
                               and an empty search result still offers it. */
                            <CommandGroup className="border-border -mx-1 border-t px-1 pt-1">
                                <CommandItem
                                    value={`action ${action.label}`}
                                    onSelect={() => {
                                        setOpen(false);
                                        action.onSelect();
                                    }}
                                >
                                    {action.icon}
                                    <span className="truncate font-medium">{action.label}</span>
                                </CommandItem>
                            </CommandGroup>
                        ) : null}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

export { Combobox };
